import { CheckCircle2, Clock3, ExternalLink, Info, MessageCircle, TriangleAlert, X } from "lucide-react";
import QRCode from "qrcode";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import useSWR from "swr";
import { PAYMENT_POLL_INTERVAL_DEFAULT_SECONDS, type CheckoutData } from "@/shared/contracts";
import { swrFetcher } from "@/web/api";
import { Badge } from "@/web/components/ui/badge";
import { Button } from "@/web/components/ui/button";
import { Card, CardContent } from "@/web/components/ui/card";
import { Loading } from "@/web/components/ui/loading";

const FEEDBACK_QQ_GROUP_URL = "https://qm.qq.com/q/oBzfX2F5mw";

function countdown(milliseconds: number) {
  const seconds = Math.max(0, Math.ceil(milliseconds / 1_000));
  return `${String(Math.floor(seconds / 60)).padStart(2, "0")}:${String(seconds % 60).padStart(2, "0")}`;
}

function OpenAlipayButton({ uri, prominent = false }: { uri: string; prominent?: boolean }) {
  const className = prominent ? "border-primary text-primary hover:bg-primary/5" : undefined;
  if (!uri) {
    return (
      <Button variant="outline" type="button" disabled className={className}>
        打开支付宝支付
      </Button>
    );
  }
  return (
    <Button variant="outline" className={className} asChild>
      <a href={uri}>打开支付宝支付</a>
    </Button>
  );
}

export function CheckoutPage() {
  const { token = "" } = useParams();
  const [now, setNow] = useState(Date.now());
  const [checkingPaid, setCheckingPaid] = useState(false);
  const [paidHint, setPaidHint] = useState("");
  const { data, error, isLoading, mutate } = useSWR<CheckoutData>(`/public-api/checkout/${encodeURIComponent(token)}`, swrFetcher, {
    refreshInterval: (latest) => {
      if (latest && (["paid", "late_paid"].includes(latest.status) || Date.parse(latest.monitor_until) <= Date.now())) return 0;
      return (latest?.payment_poll_interval_seconds ?? PAYMENT_POLL_INTERVAL_DEFAULT_SECONDS) * 1_000;
    },
    shouldRetryOnError: false,
  });
  const { data: generatedQr } = useSWR(
    data?.collection_mode === "transfer" && data.payment_uri ? ["transfer-qr", data.payment_uri] : null,
    ([, uri]) => QRCode.toDataURL(uri, { width: 300, margin: 2, errorCorrectionLevel: "M", color: { dark: "#0f172a", light: "#ffffff" } }),
  );

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(timer);
  }, []);

  const handleCancel = () => {
    if (window.history.length > 1) window.history.back();
  };

  const handlePaid = async () => {
    setCheckingPaid(true);
    try {
      const latest = await mutate();
      if (!latest || (latest.status !== "paid" && latest.status !== "late_paid")) {
        setPaidHint("暂未确认到账，请稍候，页面会自动刷新。");
      } else {
        setPaidHint("");
      }
    } catch {
      setPaidHint("暂未确认到账，请稍候，页面会自动刷新。");
    } finally {
      setCheckingPaid(false);
    }
  };

  if (isLoading) return <Loading label="正在读取支付订单" />;
  if (error || !data) return <main className="flex min-h-screen items-center justify-center px-4"><div className="max-w-sm text-center"><TriangleAlert className="mx-auto size-9 text-destructive" /><h1 className="mt-4 text-xl font-semibold">订单不存在</h1><p className="mt-2 text-sm text-muted">链接可能无效，或订单信息无法读取。</p></div></main>;

  const paid = data.status === "paid" || data.status === "late_paid";
  const checkoutExpired = now >= Date.parse(data.expires_at);
  const monitoringEnded = now >= Date.parse(data.monitor_until);

  if (paid) {
    return (
      <main className="flex min-h-screen items-center justify-center px-4 py-12">
        <Card className="w-full max-w-md"><CardContent className="px-6 py-10 text-center"><CheckCircle2 className="mx-auto size-12 text-success" /><Badge className="mt-5" variant={data.status === "late_paid" ? "primary" : "success"}>{data.status === "late_paid" ? "迟到支付已确认" : "支付成功"}</Badge><h1 className="mt-4 text-2xl font-semibold tracking-tight">已收到 ¥{data.payable_money}</h1><p className="mt-2 text-sm text-muted">订单 {data.out_trade_no} 已完成，商户通知正在后台投递。</p>{data.return_target ? <Button className="mt-6 w-full" asChild><a href={data.return_target}>返回商户页面<ExternalLink /></a></Button> : null}<Button className="mt-3 w-full" variant="outline" asChild><a href={FEEDBACK_QQ_GROUP_URL} target="_blank" rel="noreferrer">反馈QQ群：钱咖api<MessageCircle /></a></Button><p className="mt-5 text-xs text-muted">支付结果以商户服务器验签后的异步通知为准。</p></CardContent></Card>
      </main>
    );
  }

  if (monitoringEnded) {
    return (
      <main className="flex min-h-screen items-center justify-center px-4 py-12"><Card className="w-full max-w-md"><CardContent className="px-6 py-10 text-center"><Clock3 className="mx-auto size-10 text-destructive" /><h1 className="mt-4 text-xl font-semibold">订单确认窗口已结束</h1><p className="mt-2 text-sm leading-6 text-muted">系统未在 10 分钟内匹配到支付。请不要继续付款，并返回商户重新创建订单。</p><div className="mt-6 rounded-md border p-3 text-left text-xs text-muted">商户订单号：<span className="font-mono text-foreground">{data.out_trade_no}</span></div></CardContent></Card></main>
    );
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-[#6b7280] px-4 py-8">
      <section role="dialog" aria-modal="true" aria-labelledby="scan-pay-title" className="relative w-full max-w-[440px] rounded-lg bg-background px-5 py-5 shadow-xl">
        <header className="flex items-center justify-between gap-3">
          <h1 id="scan-pay-title" className="text-base font-medium">扫码支付</h1>
          <button type="button" aria-label="关闭" onClick={handleCancel} className="rounded-md p-1 text-muted hover:bg-foreground/5 hover:text-foreground">
            <X className="size-4" />
          </button>
        </header>

        <div className="mt-4 flex gap-2.5 rounded-md border border-[#91caff] bg-[#e6f4ff] px-3 py-3 text-sm leading-6 text-foreground">
          <Info className="mt-1 size-4 shrink-0 text-primary" />
          <p>请使用支付宝扫描下方二维码完成支付，也可点击「打开支付宝支付」。支付成功后将自动到账。</p>
        </div>

        <div className="mt-3 flex gap-2.5 rounded-md border border-[#ffd591] bg-[#fff7e6] px-3 py-3 text-sm leading-6 text-foreground">
          <TriangleAlert className="mt-1 size-4 shrink-0 text-[#faad14]" />
          <p>出现交易风险时，请点击「查看详情」，然后返回，再重新支付即可。</p>
        </div>

        <p className="mt-4 text-center text-sm text-primary">请使用支付宝扫描下方二维码，或点击「打开支付宝支付」</p>
        <p className="mt-3 text-center text-2xl font-semibold tracking-tight">¥{data.payable_money}</p>
        {data.payable_money !== data.requested_money ? <p className="mt-1 text-center text-xs leading-5 text-muted">请按显示金额准确支付。</p> : null}
        {data.collection_mode === "transfer" ? <p className="mt-1 text-center text-xs leading-5 text-muted">转账备注请保持为 {data.out_trade_no}，不要修改。</p> : <p className="mt-1 text-center text-xs leading-5 text-muted">扫码后请手动输入上方金额。</p>}

        <div className="mt-4 flex min-h-[240px] items-center justify-center">
          {data.collection_mode === "business_qr" ? <img src={data.business_qr_url} alt="支付宝经营码" className="max-h-[270px] max-w-full object-contain" /> : generatedQr ? <img src={generatedQr} alt="支付宝转账二维码" className="size-[270px] max-w-full object-contain" /> : <Loading label="正在生成二维码" />}
        </div>

        <div className="mt-3 flex justify-center">
          <OpenAlipayButton uri={data.payment_uri} prominent />
        </div>
        <p className="mt-3 text-center text-xs text-muted">二维码有效期 5 分钟，支付成功后自动到账</p>
        {checkoutExpired ? <p className="mt-2 text-center text-xs leading-5 text-destructive">5 分钟下单时间已过。如果尚未付款，请返回商户重新下单。剩余确认时间 {countdown(Date.parse(data.monitor_until) - now)}。</p> : null}
        {paidHint ? <p className="mt-2 text-center text-xs text-muted">{paidHint}</p> : null}

        <div className="mt-4 flex items-center justify-end gap-2">
          <Button variant="outline" type="button" onClick={handleCancel}>取消</Button>
          <OpenAlipayButton uri={data.payment_uri} />
          <Button type="button" onClick={() => void handlePaid()} disabled={checkingPaid}>{checkingPaid ? "确认中" : "我已支付"}</Button>
        </div>
      </section>
    </main>
  );
}
